'use client';

import { useState } from "react";
import { Send, Mail, MapPin, Clock } from "lucide-react";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";

export default function Contact() {
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setResult("Sending...");
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.append("access_key", "764428f4-acc5-4c33-9a92-8b86f8d30a64");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setResult("Message sent successfully!");
        setError("");
        form.reset();
      } else {
        setError(data.message || "Something went wrong. Please try again.");
        setResult("");
      }
    } catch (error) {
      setError("Something went wrong. Please try again.");
      setResult("");
    }
  };

  return (
    <main className="min-h-screen text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.contact} />
      <section className="max-w-6xl mx-auto px-6 py-4">
        <div className="border border-standard backdrop-blur-sm p-8 sm:p-12 transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] opacity-100 translate-y-0">
          {/* Main Header */}
          <div className="mb-8 sm:mb-12">
            <h1 className="h1 mb-4 sm:mb-6">Get in Touch</h1>
            <p className="body-lg max-w-3xl">
              Ready to turn ambition into impact? Share your challenge and we'll respond within 24 hours.
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-8 sm:gap-16">
            {/* Left: Contact Form */}
            <div>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    className="w-full p-3 border border-standard rounded-lg bg-transparent text-text-primary placeholder:text-text-secondary"
                    placeholder="Name"
                    required
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    className="w-full p-3 border border-standard rounded-lg bg-transparent text-text-primary placeholder:text-text-secondary"
                    placeholder="email@company.com"
                    required
                  />
                </div>

                <div>
                  <textarea
                    name="message"
                    className="w-full p-3 border border-standard rounded-lg bg-transparent text-text-primary placeholder:text-text-secondary resize-none"
                    placeholder="Tell us about your project or challenge..."
                    rows={5}
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full btn inline-flex items-center justify-center gap-2"
                >
                  Send Message <Send className="w-5 h-5" />
                </button>

                {result && (
                  <p className="mt-4 text-center text-accentcolourteal body-md font-semibold">
                    {result}
                  </p>
                )}
                {error && (
                  <p className="mt-4 text-center text-red-500 body-md font-semibold">
                    {error}
                  </p>
                )}
              </form>
            </div>

            {/* Right: Contact Details */}
            <div className="flex flex-col justify-start space-y-6">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="bg-accentcolourteal p-3 rounded-lg flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="body-md font-semibold text-text-primary mb-1">Email</p>
                  <a
                    href="mailto:contact@luminarconsult.com"
                    className="body-md hover:text-accentcolourteal transition-colors"
                  >
                    contact@luminarconsult.com
                  </a>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4">
                <div className="bg-accentcolourorange p-3 rounded-lg flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="body-md font-semibold text-text-primary mb-1">Location</p>
                  <p className="body-md">
                    Poole, United Kingdom
                  </p>
                </div>
              </div>

              {/* Response Time */}
              <div className="flex items-start gap-4">
                <div className="bg-accentcolourteal p-3 rounded-lg flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="body-md font-semibold text-text-primary mb-1">Response Time</p>
                  <p className="body-md">
                    Within 24 hours on business days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}